import { Spacing } from '@persona-id/design-tokens';
import { MinusIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { max, min } from 'lodash/fp';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import usePanZoomBoxTransition from 'dashboard/components-lib/PanZoomBox/usePanZoomBoxTransition';
import {
  applyScalingOnPoint,
  getIdentityTransformationMatrix2D,
  getTransformationMatrix2DParams,
  TransformationMatrix2D,
} from 'dashboard/lib/utils/cssTransformationMatrix';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Stack from 'design-system/components/Stack';
import useDebounced from 'lib/hooks/useDebounced';
import useDragScroll, { AttachUndraggable } from 'lib/hooks/useDragScroll';
import { useKeydownZoomInListener, useKeydownZoomOutListener } from 'lib/hooks/useKeyListener';
import usePanZoomOnWheel from 'lib/hooks/usePanZoomOnWheel';

const PanZoomGlobalStyles = createGlobalStyle`
  /* Prevents horizontal overscroll behavior from triggering navigation changes */
  html {
    overscroll-behavior-x: contain;
  }
`;

const ABSOLUTE_MINIMUM_SCALING = 0.05;
const MINIMUM_SCALING = 0.3;
const MAXIMUM_SCALING = 1.6;
const ZOOM_IN_SCALING = 1.25;
const ZOOM_OUT_SCALING = 0.7;

const ButtonWrapper = styled.div`
  position: absolute;
  right: ${Spacing[4]};
  margin-top: ${Spacing[4]};
  z-index: ${Z_INDICES.withShadow};
`;

const ScrollBoxWrapper = styled.div<{
  usePrimaryTheme?: boolean;
  useDottedBackground?: boolean;
}>`
  position: relative; /* To position button group */
  display: flex;
  background-color: ${({ theme, usePrimaryTheme }) =>
    usePrimaryTheme ? theme.color.background.primary : theme.color.background.secondary};
  overflow: hidden;
  width: 100%;
  height: 100%;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
  ${({ useDottedBackground, theme }) => {
    return (
      useDottedBackground &&
      `
      background-image: radial-gradient(${theme.color.background.secondary} 5%, transparent 5%),
                        radial-gradient(${theme.color.background.primary} 5%, transparent 5%);
      background-position: 0 0, 25px 25px;
      background-size: 50px 50px;
    `
    );
  }}
`;

const ScrollBoxContainer = styled.div`
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const ZoomButtonGroup = styled(Stack)`
  position: absolute;
  bottom: ${Spacing[4]};
  left: ${Spacing[4]};
  z-index: ${Z_INDICES.withShadow};
  grid-auto-flow: row;
`;

type PanZoomBoxZoomControls = {
  maxScale: number;
  minScale: number;
  percentage: number;
  scaleOnPoint: (scale: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
};

export interface PanZoomBoxContextValue {
  scrollRef: React.RefObject<HTMLDivElement>;
  transformationMatrixRef: React.RefObject<TransformationMatrix2D>;
  /**
   * The PanZoomBox applies a transform: matrix to this node.
   * The node that receives this ref should have the following style:
   * transform-origin: 0 0;
   */
  zoomableRef: React.RefObject<HTMLDivElement>;
  zoomControls?: PanZoomBoxZoomControls;
  isTransforming: boolean;
  /**
   * Used to mark a node to be ignored for dragging. If a node in the
   * PanZoomBox is itself draggable, it should be passed this callback.
   */
  applyScale: () => void;
  attachUndraggable: AttachUndraggable;
  centerTarget: (target: { dom: HTMLElement; scale?: number }) => void;
  centerArrayTargets: (target: { doms: HTMLElement[]; scale?: number }) => void;
  onTransformationChangeStart?: () => void;
  panScreen: (translateX: number, translateY: number) => boolean;
  resetZoom: (scale?: number) => void;
  setTransitionCallback: (value: () => void) => void;
  setTransformationMatrix: (matrix: TransformationMatrix2D) => void;
}

const PanZoomBoxContext = createContext<PanZoomBoxContextValue | undefined>(undefined);

export const usePanZoomBoxControls = (): PanZoomBoxContextValue => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const ctx = useContext<PanZoomBoxContextValue>(PanZoomBoxContext);
  return ctx;
};

export interface PanZoomBoxProps {
  children: React.ReactNode;
  className?: string;
  /**
   * utility button located in top right for adding additional actions
   */
  utilityButton?: React.ReactNode;
  overlayContent?: React.ReactNode;
  initialTransformationMatrix?: TransformationMatrix2D;
  preventPageScrollOnPan?: boolean;
  preventPanOnScroll?: boolean;
  scrollBoxBackgroundType?: 'dotted';
  scrollBoxUsePrimaryThemeColor?: boolean;
  showZoomControls?: boolean;
  useKeydownShortcuts?: boolean;
  /**
   * exposes transformation (pan/zoom) start and end events to update canvas nodes
   */
  withDynamicUpdates?: boolean;
  /**
   * exposes zoom controls and data (percentage for display) for external component use
   */
  withExternalZoomControls?: boolean;
  zoomToFit?: boolean;
  /**
   * the amount of padding to add when zoomed to fit
   */
  zoomToFitPadding?: { top?: number; bottom?: number; left?: number; right?: number };
  onTransformationMatrixChange?: (transformationMatrix: TransformationMatrix2D) => void;
}

/**
 * A container that allows panning and zooming.
 * Must be rendered inside a container with a fixed height.
 */
const PanZoomBox = ({
  children,
  className,
  utilityButton,
  overlayContent,
  initialTransformationMatrix,
  preventPageScrollOnPan,
  preventPanOnScroll,
  scrollBoxBackgroundType,
  scrollBoxUsePrimaryThemeColor,
  showZoomControls = true,
  useKeydownShortcuts,
  withDynamicUpdates,
  withExternalZoomControls,
  zoomToFit,
  zoomToFitPadding = {},
  onTransformationMatrixChange,
}: PanZoomBoxProps) => {
  const transformationMatrixRef = useRef<TransformationMatrix2D>(
    getIdentityTransformationMatrix2D(),
  );

  // minimum scale can change if we enable zoomToFit
  const [minimumScale, setMinimumScale] = useState(MINIMUM_SCALING);

  // placeholder function because we need the panScreen from usePanZoomOnWheel
  const panScreenRef = useRef<(x: number, y: number) => boolean>((x: number, y: number) => {
    return x === y;
  });
  const [scrollRef, attachUndraggable] = useDragScroll(panScreenRef);
  const [percentageScale, setPercentageScale] = useState(MINIMUM_SCALING * 100);
  const [debouncedSetPercentageScale] = useDebounced(setPercentageScale, 500, {
    leading: false,
    trailing: true,
  });

  const [isTransforming, setIsTransforming] = useState<boolean>(false);
  const [debouncedSetIsTransforming] = useDebounced(setIsTransforming, 400, {
    leading: false,
    trailing: true,
  });

  const handleTransformationChangeStart = useCallback(() => {
    setIsTransforming(true);
  }, [setIsTransforming]);

  const handleTransformationChangeEnd = useCallback(
    (matrix: TransformationMatrix2D) => {
      onTransformationMatrixChange?.(matrix);
      setTimeout(() => debouncedSetIsTransforming(false), 250);
    },
    [debouncedSetIsTransforming, onTransformationMatrixChange],
  );

  const { zoomableRef, containerRef, scaleScreen, panScreen, setTransformationMatrix } =
    usePanZoomOnWheel({
      minimumScaling: minimumScale,
      maximumScaling: MAXIMUM_SCALING,
      transformationMatrixRef,
      boundingRef: scrollRef,
      preventDefaultOnPan: preventPageScrollOnPan,
      preventPanOnScroll: preventPanOnScroll,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onTransformationChangeStart: withDynamicUpdates ? handleTransformationChangeStart : null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onTransformationMatrixChange: withDynamicUpdates ? handleTransformationChangeEnd : null,
      setPercentageScale: debouncedSetPercentageScale,
    });
  panScreenRef.current = panScreen;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { setTransitionCallback } = usePanZoomBoxTransition(zoomableRef.current, 200);

  /**
   * An optional matrix param is used here to set the initial percentage scale from useLayoutEffect
   * because we use requestAnimationFrame to wrap the setTransformationMatrix logic in usePanZoomOnWheel,
   * which means transformationMatrixRef.current is not updated immediately.
   */
  const applyPercentageScale = useCallback((matrix?: TransformationMatrix2D) => {
    const currentScale = getTransformationMatrix2DParams(
      matrix ?? transformationMatrixRef.current,
    ).scaleX;
    // round scale to the nearest whole number
    setPercentageScale(Math.round(currentScale * 100));
  }, []);

  const calculateTrueMinScale = useCallback(() => {
    if (!zoomableRef.current || !scrollRef.current) return;

    const {
      top: zoomToFitPaddingTop = 0,
      bottom: zoomToFitPaddingBottom = 0,
      left: zoomToFitPaddingLeft = 0,
      right: zoomToFitPaddingRight = 0,
    } = zoomToFitPadding;

    const { clientHeight, clientWidth } = zoomableRef.current;

    const availableHeight =
      scrollRef.current.clientHeight - zoomToFitPaddingTop - zoomToFitPaddingBottom;
    const availableWidth =
      scrollRef.current.clientWidth - zoomToFitPaddingLeft - zoomToFitPaddingRight;

    const scaleToFit = Math.max(
      Math.min(availableHeight / clientHeight, availableWidth / clientWidth, 1),
      ABSOLUTE_MINIMUM_SCALING,
    );

    return scaleToFit < minimumScale ? parseFloat(scaleToFit.toFixed(2)) : minimumScale;
  }, [zoomableRef, scrollRef, minimumScale, zoomToFitPadding]);

  useLayoutEffect(() => {
    const shouldCalculateZoomToFit = zoomToFit && scrollRef.current && zoomableRef.current;
    const {
      top: zoomToFitPaddingTop = 0,
      bottom: zoomToFitPaddingBottom = 0,
      left: zoomToFitPaddingLeft = 0,
      right: zoomToFitPaddingRight = 0,
    } = zoomToFitPadding;

    let trueMinimumScale = minimumScale;

    if (shouldCalculateZoomToFit) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      trueMinimumScale = calculateTrueMinScale();
      setMinimumScale(trueMinimumScale);
    }

    if (initialTransformationMatrix) {
      setTransformationMatrix(initialTransformationMatrix);
      applyPercentageScale(initialTransformationMatrix);
    } else if (shouldCalculateZoomToFit) {
      // after scaling, this is the position of the left edge from the side of scroll container
      const currentXPos =
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        zoomableRef.current.getBoundingClientRect().left -
        scrollRef.current.getBoundingClientRect().left;
      // want to position the left edge so there's equal space on either side
      const targetXPos =
        (zoomToFitPaddingLeft -
          zoomToFitPaddingRight +
          scrollRef.current.clientWidth -
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          zoomableRef.current.clientWidth * trueMinimumScale) /
        2;
      const translateX = targetXPos - currentXPos;

      // after scaling, this is the position of the top edge from the top of scroll container
      const currentYPos = 0;
      // want to position the top edge so there's equal space on either side
      const targetYPos =
        (zoomToFitPaddingTop -
          zoomToFitPaddingBottom +
          scrollRef.current.clientHeight -
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          zoomableRef.current.clientHeight * trueMinimumScale) /
        2;
      const translateY = targetYPos - currentYPos;
      const initialZoomToFitMatrix: TransformationMatrix2D = [
        [trueMinimumScale, 0, translateX],
        [0, trueMinimumScale, translateY],
        [0, 0, 1],
      ];
      setTransformationMatrix(initialZoomToFitMatrix);
      applyPercentageScale(initialZoomToFitMatrix);
    }
  }, []);

  const getMidpointXY = useCallback(() => {
    let originX = null;
    let originY = null;
    if (zoomableRef.current && scrollRef.current) {
      const workflowTreeRect = zoomableRef.current.getBoundingClientRect();
      const scrollRect = scrollRef.current.getBoundingClientRect();
      originX = (scrollRect.right + scrollRect.left) / 2 - workflowTreeRect.left;
      originY = (scrollRect.top + scrollRect.bottom) / 2 - workflowTreeRect.top;
    } else {
      originX = window.innerWidth / 2;
      originY = window.innerHeight / 2;
    }

    return { originX, originY };
  }, [zoomableRef, scrollRef]);

  const zoomIn = useCallback(() => {
    const currentScale = getTransformationMatrix2DParams(transformationMatrixRef.current).scaleX;
    const { originX, originY } = getMidpointXY();
    const newTransformationMatrix = scaleScreen(
      Math.min(MAXIMUM_SCALING / currentScale, ZOOM_IN_SCALING),
      originX,
      originY,
    );
    applyPercentageScale(newTransformationMatrix);
  }, [applyPercentageScale, getMidpointXY, scaleScreen]);

  const zoomOut = useCallback(() => {
    const currentScale = getTransformationMatrix2DParams(transformationMatrixRef.current).scaleX;
    const { originX, originY } = getMidpointXY();
    const newTransformationMatrix = scaleScreen(
      Math.max(minimumScale / currentScale, ZOOM_OUT_SCALING),
      originX,
      originY,
    );
    applyPercentageScale(newTransformationMatrix);
  }, [applyPercentageScale, getMidpointXY, minimumScale, scaleScreen]);

  const resetZoom = useCallback(
    (scale?: number) => {
      const currentScale = getTransformationMatrix2DParams(transformationMatrixRef.current).scaleX;
      const { originX, originY } = getMidpointXY();
      scaleScreen(scale ?? 1.0 / currentScale, originX, originY);
    },
    [getMidpointXY, scaleScreen],
  );

  const scaleOnPoint = useCallback(
    (scale: number) => {
      const {
        scaleX: currentScale,
        translateX,
        translateY,
      } = getTransformationMatrix2DParams(transformationMatrixRef.current);

      const clampedScale = Math.min(Math.max(minimumScale, scale), MAXIMUM_SCALING);

      const { originX, originY } = getMidpointXY();
      const newMatrix = applyScalingOnPoint(
        [
          [currentScale, 0, translateX],
          [0, currentScale, translateY],
          [0, 0, 1],
        ],
        clampedScale / currentScale,
        originX,
        originY,
      );

      setTransformationMatrix(newMatrix);
      applyPercentageScale(newMatrix);
    },
    [applyPercentageScale, getMidpointXY, minimumScale, setTransformationMatrix],
  );

  // Given the existing scale, and a bounding rect representing the scaled target, calculate
  // the new scale such that the target is zoomed to fit with the given padding
  const calculateAdjustedScaleToFit = useCallback(
    (
      domRect: DOMRect,
      currentScale: number,
      zoomToFitPadding: { top?: number; bottom?: number; left?: number; right?: number },
    ) => {
      const {
        top: zoomToFitPaddingTop = 0,
        bottom: zoomToFitPaddingBottom = 0,
        left: zoomToFitPaddingLeft = 0,
        right: zoomToFitPaddingRight = 0,
      } = zoomToFitPadding;

      const availableHeight =
        scrollRef.current.clientHeight - zoomToFitPaddingTop - zoomToFitPaddingBottom;
      const availableWidth =
        scrollRef.current.clientWidth - zoomToFitPaddingLeft - zoomToFitPaddingRight;

      // Scale the currentScale so that the scaled domRect will be zoomed to fit
      const scaleToFitHeight = currentScale * (availableHeight / domRect.height);
      const scaleToFitWidth = currentScale * (availableWidth / domRect.width);

      return Math.max(Math.min(scaleToFitHeight, scaleToFitWidth, 1), ABSOLUTE_MINIMUM_SCALING);
    },
    [scrollRef],
  );

  const zoomControls = useMemo(
    () => ({
      zoomIn,
      zoomOut,
      scaleOnPoint,
      percentage: percentageScale,
      minScale: minimumScale,
      maxScale: MAXIMUM_SCALING,
    }),
    // This only needs to update when the scaling percentages change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [MAXIMUM_SCALING, minimumScale, percentageScale],
  );

  const contextZoomControls = useMemo(
    () => (withExternalZoomControls ? zoomControls : null),
    [withExternalZoomControls, zoomControls],
  );

  const isTransformingValue = withDynamicUpdates ? isTransforming : false;
  const onTransformationChangeStartFn = withDynamicUpdates ? handleTransformationChangeStart : null;

  const centerOnBoundingRect = useCallback(
    (targetRect: DOMRect, targetScale?: number) => {
      if (!zoomableRef.current || !scrollRef.current) {
        return;
      }

      const zoomableRect = zoomableRef.current.getBoundingClientRect();
      const scrollRect = scrollRef.current.getBoundingClientRect();

      // our starting midpoints
      const originX = (scrollRect.right + scrollRect.left) / 2 - zoomableRect.left;
      const originY = (scrollRect.top + scrollRect.bottom) / 2 - zoomableRect.top;

      // grab current transformation values
      const transformationMatrix = getTransformationMatrix2DParams(transformationMatrixRef.current);
      const currentScale = transformationMatrix.scaleX;
      const originalX = transformationMatrix.translateX;
      const originalY = transformationMatrix.translateY;

      // calculate the translation amount needed to center on the desired x using the
      // scaled width of the target
      const currentXPos = targetRect.left - scrollRect.left;
      const targetXPos = (scrollRef.current.clientWidth - targetRect.width) / 2;

      const translateX = targetXPos - currentXPos;

      // calculate the translation amount needed to center on the desired y using the
      // scaled width of the target
      const currentYPos = targetRect.top - scrollRect.top;
      const targetYPos = (scrollRef.current.clientHeight - targetRect.height) / 2;

      const translateY = targetYPos - currentYPos;

      if (targetScale == null) {
        // Calculate the appropriate scale based on the target
        targetScale = calculateAdjustedScaleToFit(targetRect, currentScale, zoomToFitPadding);
      }

      // Apply scaling on new midpoint
      const newMatrix = applyScalingOnPoint(
        [
          [currentScale, 0, translateX + originalX],
          [0, currentScale, translateY + originalY],
          [0, 0, 1],
        ],
        targetScale / currentScale,
        originX - translateX, // new x-center after translation
        originY - translateY, // new y-center after translation
      );

      setTransitionCallback(() => () => {
        setTransformationMatrix(newMatrix);
        applyPercentageScale(newMatrix);
      });
    },
    [
      applyPercentageScale,
      calculateAdjustedScaleToFit,
      scrollRef,
      setTransformationMatrix,
      setTransitionCallback,
      zoomableRef,
    ],
  );

  const centerTarget = useCallback(
    ({ dom: targetDom, scale: targetScale }: { dom: HTMLElement; scale?: number }) => {
      centerOnBoundingRect(targetDom.getBoundingClientRect(), targetScale);
    },
    [centerOnBoundingRect],
  );

  const centerArrayTargets = useCallback(
    ({ doms: targetDoms, scale: targetScale }: { doms: HTMLElement[]; scale?: number }) => {
      if (targetDoms.length === 0) {
        return;
      }

      const boundingRects = targetDoms.map((dom) => {
        return dom.getBoundingClientRect();
      });

      // Find the bounding rect of the collective DOM elements by calculating
      // the respective extremes of each x, y, right and bottom coordinates
      const x = min(boundingRects.map((rect) => rect.left)) as number;
      const y = min(boundingRects.map((rect) => rect.top)) as number;
      const right = max(boundingRects.map((rect) => rect.right)) as number;
      const bottom = max(boundingRects.map((rect) => rect.bottom)) as number;
      const targetDomRect = new DOMRect(x, y, right - x, bottom - y);

      centerOnBoundingRect(targetDomRect, targetScale);
    },
    [centerOnBoundingRect],
  );

  const panZoomBoxContextValue = useMemo<PanZoomBoxContextValue>(
    () => ({
      scrollRef,
      transformationMatrixRef,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      zoomableRef,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      zoomControls: contextZoomControls,
      isTransforming: isTransformingValue,
      applyScale: applyPercentageScale,
      attachUndraggable,
      centerTarget,
      centerArrayTargets,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onTransformationChangeStart: onTransformationChangeStartFn,
      panScreen,
      resetZoom,
      setTransitionCallback,
      setTransformationMatrix,
    }),
    [
      applyPercentageScale,
      attachUndraggable,
      centerTarget,
      centerArrayTargets,
      contextZoomControls,
      isTransformingValue,
      onTransformationChangeStartFn,
      panScreen,
      resetZoom,
      scrollRef,
      setTransformationMatrix,
      setTransitionCallback,
      transformationMatrixRef,
      zoomableRef,
    ],
  );

  useKeydownZoomInListener()((e) => {
    if (useKeydownShortcuts) {
      e.preventDefault();
      return zoomControls.zoomIn();
    }
  });
  useKeydownZoomOutListener()((e) => {
    if (useKeydownShortcuts) {
      e.preventDefault();
      return zoomControls.zoomOut();
    }
  });

  return (
    <PanZoomBoxContext.Provider value={panZoomBoxContextValue}>
      <PanZoomGlobalStyles />
      {overlayContent && overlayContent}
      <ScrollBoxWrapper
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={containerRef}
        usePrimaryTheme={scrollBoxUsePrimaryThemeColor}
        useDottedBackground={scrollBoxBackgroundType === 'dotted'}
        className={className}
      >
        <ScrollBoxContainer data-test='pan-zoom-box__scrollable-container' ref={scrollRef}>
          {utilityButton && <ButtonWrapper>{utilityButton}</ButtonWrapper>}
          {children}
          {showZoomControls && (
            <ZoomButtonGroup>
              <Button
                aria-label='zoom in'
                type='button'
                width='icon'
                id='pan-zoom-box__zoom-in'
                onClick={zoomIn}
              >
                <PlusIcon />
              </Button>
              <Button
                aria-label='zoom out'
                type='button'
                width='icon'
                id='pan-zoom-box__zoom-out'
                onClick={zoomOut}
              >
                <MinusIcon />
              </Button>
            </ZoomButtonGroup>
          )}
        </ScrollBoxContainer>
      </ScrollBoxWrapper>
    </PanZoomBoxContext.Provider>
  );
};

export default PanZoomBox;
